/**
 * WEG-Jahresabrechnung § 28 WEG — Übersicht
 * ==========================================
 * Zeigt alle Jahresabrechnungen pro WEG-Objekt mit Status.
 * Detail-Ansicht folgt in Phase 2 — vorerst als schlankes Read-Only-Dashboard
 * mit Direktzugriff auf PDF-Export der Einzelabrechnungen.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatEUR } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Buildings, Calculator, FilePdf, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { openAuthenticatedPdf } from "@/lib/pdfUtils";

export default function WegJahresabrechnung() {
  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // aktive JA
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: ja }, { data: props }] = await Promise.all([
        api.get("/weg-ja"),
        api.get("/properties"),
      ]);
      setRows(ja);
      setProperties(props.filter((p) => ["weg", "mixed"].includes(p.object_type)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="wegja-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Buildings size={24} className="text-emerald-700" />
            WEG-Jahresabrechnung § 28 WEG
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Vermögensübersicht, Einnahmen/Ausgaben und Einzelabrechnung nach MEA für jede WEG.
            Aus jeder JA kann die Mieter-BKA automatisch abgeleitet werden.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="wegja-new-btn" className="gap-1.5">
              <Plus size={16} /> Neue Jahresabrechnung
            </Button>
          </DialogTrigger>
          <NewJaDialog properties={properties} onCreated={() => { load(); setDialogOpen(false); }} />
        </Dialog>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-10 text-center">Lade…</div>
      ) : detail ? (
        <JaDetail ja={detail} onClose={() => { setDetail(null); load(); }} />
      ) : rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-14 text-center bg-slate-50/50">
          <Buildings size={40} className="mx-auto text-slate-300 mb-3" />
          <div className="text-slate-500">Noch keine Jahresabrechnung angelegt.</div>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2">Objekt</th>
                <th className="text-left px-3 py-2 w-24">Jahr</th>
                <th className="text-left px-3 py-2 w-36">Zeitraum</th>
                <th className="text-right px-3 py-2 w-24">Buchungen</th>
                <th className="text-center px-3 py-2 w-28">Status</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`wegja-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium">{r.property_name || "—"}</td>
                  <td className="px-3 py-2 tabular-nums">{r.year}</td>
                  <td className="px-3 py-2 text-xs tabular-nums">
                    {r.period_from?.slice(0, 10)} – {r.period_to?.slice(0, 10)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.entry_count ?? 0}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                      r.status === "locked" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                      : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right">
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => setDetail(r)}
                            data-testid={`wegja-open-${r.id}`}>
                      Öffnen
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NewJaDialog({ properties, onCreated }) {
  const [propertyId, setPropertyId] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear() - 1));
  const [bankStart, setBankStart] = useState("");
  const [bankEnd, setBankEnd] = useState("");
  const [reserveStart, setReserveStart] = useState("");
  const [reserveEnd, setReserveEnd] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!propertyId) return;
    setSaving(true);
    try {
      await api.post("/weg-ja", {
        property_id: propertyId,
        year: parseInt(year),
        period_from: `${year}-01-01`,
        period_to: `${year}-12-31`,
        bank_balance_start: bankStart ? parseFloat(bankStart) : null,
        bank_balance_end: bankEnd ? parseFloat(bankEnd) : null,
        reserve_balance_start: reserveStart ? parseFloat(reserveStart) : null,
        reserve_balance_end: reserveEnd ? parseFloat(reserveEnd) : null,
      });
      toast.success("Jahresabrechnung angelegt");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };
  return (
    <DialogContent className="max-w-lg" data-testid="wegja-new-dialog">
      <DialogHeader>
        <DialogTitle>Neue WEG-Jahresabrechnung</DialogTitle>
        <DialogDescription>Vermögensstände können später ergänzt werden.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label>WEG-Objekt</Label>
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger data-testid="wegja-property"><SelectValue placeholder="Objekt wählen" /></SelectTrigger>
            <SelectContent>
              {properties.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}{p.property_number ? ` · ${p.property_number}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {properties.length === 0 && <p className="text-[10px] text-rose-600 mt-1">Kein WEG-Objekt vorhanden. Bitte Objektart auf „WEG" setzen.</p>}
        </div>
        <div>
          <Label>Wirtschaftsjahr</Label>
          <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="wegja-year" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Bankstand 01.01. (EUR)</Label>
            <Input type="number" step="0.01" value={bankStart} onChange={(e) => setBankStart(e.target.value)} /></div>
          <div><Label>Bankstand 31.12. (EUR)</Label>
            <Input type="number" step="0.01" value={bankEnd} onChange={(e) => setBankEnd(e.target.value)} /></div>
          <div><Label>Rücklage 01.01.</Label>
            <Input type="number" step="0.01" value={reserveStart} onChange={(e) => setReserveStart(e.target.value)} /></div>
          <div><Label>Rücklage 31.12.</Label>
            <Input type="number" step="0.01" value={reserveEnd} onChange={(e) => setReserveEnd(e.target.value)} /></div>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={!propertyId || saving} data-testid="wegja-submit">
          {saving ? "Speichere…" : "Anlegen"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function JaDetail({ ja: initial, onClose }) {
  const [ja, setJa] = useState(initial);
  const [owners, setOwners] = useState([]);
  const [entryOpen, setEntryOpen] = useState(false);
  const [computing, setComputing] = useState(false);
  const reload = async () => {
    const { data } = await api.get(`/weg-ja/${ja.id}`);
    setJa(data);
    const oa = await api.get(`/weg-ja/${ja.id}/owner-abrechnungen`);
    setOwners(oa.data || []);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const isDraft = ja.status === "draft";
  const calc = async () => {
    setComputing(true);
    try {
      const { data } = await api.post(`/weg-ja/${ja.id}/calculate`);
      toast.success(`${data.abrechnungen_created} Einzelabrechnung(en) erzeugt`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setComputing(false); }
  };
  const deriveBka = async () => {
    if (!confirm("Erzeugt eine BKA-Periode aus den Positionen dieser WEG-JA. Alle Positionen (auch nicht-umlagefähige zur Information) werden übernommen. Fortfahren?")) return;
    try {
      const { data } = await api.post(`/weg-ja/${ja.id}/derive-bka`);
      const infoNote = data.non_umlage_count
        ? ` (davon ${data.non_umlage_count} nicht-umlagefähig — nur zur Info)` : "";
      toast.success(`Mieter-BKA angelegt mit ${data.positions_created} Positionen${infoNote}`);
      window.open(`/betriebskosten/${data.bka_period_id}`, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };
  // iter154 — Wirtschaftsplan-Folgejahr erzeugen
  const [wpDialogOpen, setWpDialogOpen] = useState(false);
  const [wpPct, setWpPct] = useState("0");
  const [wpSaving, setWpSaving] = useState(false);
  const createNextYearPlan = async () => {
    setWpSaving(true);
    try {
      const { data } = await api.post(`/weg-ja/${ja.id}/create-plan-nextyear`, {
        increase_pct: parseFloat(wpPct || "0"),
      });
      toast.success(`Wirtschaftsplan ${data.year} angelegt (${data.positions_created} Positionen, ${data.total_planned.toLocaleString("de-DE")} €)`);
      setWpDialogOpen(false);
      setWpPct("0");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setWpSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button size="sm" variant="ghost" onClick={onClose}>← Zurück zur Liste</Button>
        <div className="flex gap-2">
          {isDraft && (
            <Dialog open={entryOpen} onOpenChange={setEntryOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="gap-1.5" data-testid="entry-add">
                  <Plus size={12} /> Buchung
                </Button>
              </DialogTrigger>
              <EntryDialog jaId={ja.id} onSaved={() => { setEntryOpen(false); reload(); }} />
            </Dialog>
          )}
          <Button size="sm" onClick={calc} disabled={computing} className="gap-1.5" data-testid="wegja-calc">
            <Calculator size={12} /> {computing ? "Berechne…" : "Einzelabrechnungen"}
          </Button>
          <Button size="sm" variant="outline" onClick={deriveBka} className="gap-1.5" data-testid="wegja-derive-bka">
            <ArrowClockwise size={12} /> Mieter-BKA ableiten
          </Button>
          {/* iter154 — Wirtschaftsplan Folgejahr erzeugen */}
          <Dialog open={wpDialogOpen} onOpenChange={setWpDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="gap-1.5" data-testid="wegja-create-plan">
                <Plus size={12} /> Wirtschaftsplan {parseInt(ja.year) + 1}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" data-testid="wp-nextyear-dialog">
              <DialogHeader>
                <DialogTitle>Wirtschaftsplan {parseInt(ja.year) + 1} erstellen</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2 text-sm">
                <p className="text-slate-600">
                  Die Ist-Ausgaben aus der WEG-Jahresabrechnung {ja.year} werden je Kategorie zusammengeführt
                  und als Entwurf für {parseInt(ja.year) + 1} übernommen. Optional kann eine prozentuale
                  Erhöhung angesetzt werden (z. B. Inflation, gestiegene Energiepreise).
                </p>
                <div>
                  <Label htmlFor="wp-pct">Prozentuale Erhöhung</Label>
                  <div className="relative mt-1">
                    <Input id="wp-pct" type="number" step="0.5" value={wpPct}
                           onChange={(e) => setWpPct(e.target.value)}
                           placeholder="0" data-testid="wp-pct-input" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs">%</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    0 = 1:1 übernehmen · positiv = erhöht · negativ = reduziert
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setWpDialogOpen(false)}>Abbrechen</Button>
                <Button size="sm" onClick={createNextYearPlan} disabled={wpSaving} data-testid="wp-nextyear-submit">
                  {wpSaving ? "Erstelle …" : `Wirtschaftsplan ${parseInt(ja.year) + 1} anlegen`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Vermögensübersicht */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Einnahmen (Giro)" value={formatEUR(ja.total_einnahmen)} />
        <Kpi label="Ausgaben (Giro)" value={formatEUR(ja.total_ausgaben)} />
        <Kpi label="Umlagefähig" value={formatEUR(ja.total_umlagefaehig)} />
        <Kpi label="Rücklage-Zuführung" value={formatEUR(ja.reserve_zufuehrung)} />
      </div>
      {(ja.bank_balance_start != null || ja.reserve_balance_start != null) && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">
          <div className="font-medium mb-1">Vermögensübersicht</div>
          <div>Girokonto: {formatEUR(ja.bank_balance_start)} → {formatEUR(ja.bank_balance_end)}
            {ja.bank_balance_delta != null && (
              <span className={Math.abs(ja.bank_balance_delta) < 0.01 ? "text-emerald-700 ml-2" : "text-rose-700 ml-2"}>
                (Differenz zum Erwartet: {formatEUR(ja.bank_balance_delta)})
              </span>
            )}
          </div>
          <div>Rücklage: {formatEUR(ja.reserve_balance_start)} → {formatEUR(ja.reserve_balance_end)}</div>
        </div>
      )}

      {/* Buchungen */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="bg-slate-50 px-3 py-2 border-b text-xs font-medium">Buchungen ({ja.entries?.length || 0})</div>
        <table className="w-full text-xs">
          <thead className="text-slate-500">
            <tr>
              <th className="text-left px-2 py-1">Datum</th>
              <th className="text-left px-2 py-1">Typ</th>
              <th className="text-left px-2 py-1">Kategorie</th>
              <th className="text-left px-2 py-1">Konto</th>
              <th className="text-right px-2 py-1">Betrag</th>
              <th className="text-left px-2 py-1">Umlage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(ja.entries || []).map((e) => (
              <tr key={e.id}>
                <td className="px-2 py-1 tabular-nums">{e.booking_date?.slice(0, 10)}</td>
                <td className="px-2 py-1">{e.entry_type}</td>
                <td className="px-2 py-1">{e.category_label || e.category_key}</td>
                <td className="px-2 py-1">
                  <span className={e.account_type === "ruecklage" ? "text-violet-700" : "text-slate-600"}>{e.account_type}</span>
                </td>
                <td className={`px-2 py-1 text-right tabular-nums ${e.entry_type === "einnahme" ? "text-emerald-700" : ""}`}>
                  {e.entry_type === "ausgabe" ? "-" : ""}{formatEUR(e.amount_gross)}
                </td>
                <td className="px-2 py-1">
                  <span className={`text-[10px] px-1 py-0.5 rounded ${
                    e.umlagefaehig === "voll" ? "bg-emerald-100 text-emerald-800"
                    : e.umlagefaehig === "teilweise" ? "bg-amber-100 text-amber-800"
                    : "bg-slate-200 text-slate-700"}`}>
                    {e.umlagefaehig}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Einzelabrechnungen */}
      {owners.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="bg-slate-50 px-3 py-2 border-b text-xs font-medium">Einzelabrechnung pro Eigentümer</div>
          <table className="w-full text-xs">
            <thead className="text-slate-500">
              <tr>
                <th className="text-left px-2 py-1">Eigentümer</th>
                <th className="text-left px-2 py-1 w-24">MEA</th>
                <th className="text-right px-2 py-1">Kostenanteil</th>
                <th className="text-right px-2 py-1">Hausgeld Ist</th>
                <th className="text-right px-2 py-1">Saldo</th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {owners.map((o) => (
                <tr key={o.id}>
                  <td className="px-2 py-1">{o.owner_name}</td>
                  <td className="px-2 py-1">{o.mea_percent.toFixed(3)}%</td>
                  <td className="px-2 py-1 text-right tabular-nums">{formatEUR(o.total_costs)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{formatEUR(o.hausgeld_ist)}</td>
                  <td className={`px-2 py-1 text-right tabular-nums font-medium ${
                    o.ergebnis_saldo > 0.01 ? "text-rose-700" : "text-emerald-700"}`}>
                    {o.ergebnis_saldo > 0.01 ? "+" : ""}{formatEUR(o.ergebnis_saldo)}
                  </td>
                  <td className="px-2 py-1 text-right">
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
                            onClick={() => openAuthenticatedPdf(`/weg-ja/owner-abrechnung/${o.id}/pdf`)}
                            data-testid={`wegja-oa-pdf-${o.id}`}>
                      <FilePdf size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-base font-semibold tabular-nums mt-0.5 text-slate-900">{value}</div>
    </div>
  );
}

const CATEGORIES_ALL = [
  { g: "BetrKV § 2", opts: [
    ["1_grundsteuer", "Grundsteuer"], ["2_wasser", "Wasser"], ["3_entwaesserung", "Entwässerung"],
    ["4a_heizung", "Heizung"], ["4b_warmwasser", "Warmwasser"],
    ["5_aufzug", "Aufzug"], ["6_muell", "Müll"], ["7_gebaeudereinigung", "Gebäudereinigung"],
    ["8_gartenpflege", "Gartenpflege"], ["9_beleuchtung", "Allgemeinstrom"],
    ["10_schornsteinfeger", "Schornsteinfeger"], ["11_versicherung_sach", "Versicherung"],
    ["12_hauswart", "Hauswart"], ["13_antenne", "Antenne / Kabel"], ["15_sonstige", "Sonstige"],
  ]},
  { g: "WEG (nicht umlagefähig)", opts: [
    ["weg_instandhaltung", "Instandhaltung"], ["weg_verwaltung", "Verwaltervergütung"],
    ["weg_ruecklage", "Rücklagenzuführung"], ["weg_bank", "Bankgebühren"],
    ["weg_rechtsberatung", "Rechts-/Steuerberatung"], ["weg_sonstige", "Sonstige"],
  ]},
];

function EntryDialog({ jaId, onSaved }) {
  const [entryType, setEntryType] = useState("ausgabe");
  const [categoryKey, setCategoryKey] = useState("");
  const [amount, setAmount] = useState("");
  const [bookingDate, setBookingDate] = useState(new Date().toISOString().slice(0, 10));
  const [accountType, setAccountType] = useState("giro");
  const [umlagefaehig, setUmlagefaehig] = useState("voll");
  const [percent, setPercent] = useState("");
  const [supplier, setSupplier] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!categoryKey || !amount) return;
    setSaving(true);
    try {
      await api.post(`/weg-ja/${jaId}/entries`, {
        ja_id: jaId,
        entry_type: entryType,
        category_key: categoryKey,
        amount_gross: parseFloat(amount),
        booking_date: bookingDate,
        account_type: accountType,
        umlagefaehig,
        umlagefaehig_percent: umlagefaehig === "teilweise" ? parseFloat(percent || 100) : null,
        supplier_or_source: supplier || null,
      });
      toast.success("Buchung hinzugefügt");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };
  return (
    <DialogContent className="max-w-md" data-testid="entry-dialog">
      <DialogHeader><DialogTitle>Buchung erfassen</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Typ</Label>
            <Select value={entryType} onValueChange={setEntryType}>
              <SelectTrigger data-testid="entry-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="einnahme">Einnahme</SelectItem>
                <SelectItem value="ausgabe">Ausgabe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Konto</Label>
            <Select value={accountType} onValueChange={setAccountType}>
              <SelectTrigger data-testid="entry-account"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="giro">Girokonto</SelectItem>
                <SelectItem value="ruecklage">Rücklage</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Kategorie</Label>
          <Select value={categoryKey} onValueChange={setCategoryKey}>
            <SelectTrigger data-testid="entry-category"><SelectValue placeholder="wählen" /></SelectTrigger>
            <SelectContent className="max-h-96">
              {CATEGORIES_ALL.map((grp) => (
                <div key={grp.g}>
                  <div className="text-[10px] px-2 py-1 uppercase text-slate-400 font-semibold">{grp.g}</div>
                  {grp.opts.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </div>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Betrag (EUR)</Label>
            <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="entry-amount" />
          </div>
          <div>
            <Label>Buchungsdatum</Label>
            <Input type="date" value={bookingDate} onChange={(e) => setBookingDate(e.target.value)} data-testid="entry-date" />
          </div>
        </div>
        {entryType === "ausgabe" && (
          <>
            <div>
              <Label>Umlagefähig auf Mieter</Label>
              <Select value={umlagefaehig} onValueChange={setUmlagefaehig}>
                <SelectTrigger data-testid="entry-umlage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="voll">Voll</SelectItem>
                  <SelectItem value="teilweise">Teilweise</SelectItem>
                  <SelectItem value="nicht">Nicht</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {umlagefaehig === "teilweise" && (
              <div>
                <Label>Umlage-Prozent</Label>
                <Input type="number" min="0" max="100" value={percent} onChange={(e) => setPercent(e.target.value)} />
              </div>
            )}
          </>
        )}
        <div>
          <Label>Aussteller/Quelle</Label>
          <Input value={supplier} onChange={(e) => setSupplier(e.target.value)}
                 placeholder="z. B. Stadtwerke" data-testid="entry-supplier" />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={!categoryKey || !amount || saving} data-testid="entry-save">
          {saving ? "Speichere…" : "Hinzufügen"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
